import React, { useState, useMemo } from "react";
import CouponCardShell from "./CouponCardShell";
import { COLORS, formatDate, formatReference } from "./couponUi";

const TEST_STATUS_LABELS = {
  AVAILABLE: "Available",
  USED: "Used",
  EXPIRED: "Expired",
};

function TestStatusBadge({ status }) {
  const key = typeof status === "string" ? status.toUpperCase() : "";
  const label = TEST_STATUS_LABELS[key] || (status ? String(status) : "Unknown");
  const styles = {
    AVAILABLE: { bg: "#ecfdf5", text: "#047857", border: "#a7f3d0" },
    USED: { bg: `${COLORS.aquaBlue}1a`, text: COLORS.aquaBlue, border: `${COLORS.aquaBlue}55` },
    EXPIRED: { bg: "#fef2f2", text: "#b91c1c", border: "#fecaca" },
  };
  const s = styles[key] || { bg: COLORS.lightGrey, text: COLORS.mediumText, border: COLORS.mediumGrey };
  return (
    <span
      style={{
        display: "inline-block",
        backgroundColor: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        borderRadius: 999,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

const thStyle = { padding: "8px 10px", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" };
const tdStyle = { padding: "8px 10px", fontSize: 12, color: COLORS.darkText };

export default function MedicalCheckupCoupon({ coupon }) {
  const c = coupon && typeof coupon === "object" ? coupon : {};
  const members = Array.isArray(c.members) ? c.members : [];

  const memberOptions = useMemo(
    () =>
      members.map((m, i) => ({
        key: m?.member_db_id ?? m?.member_id ?? i,
        label: m?.name ? (m.member_id ? `${m.name} (${m.member_id})` : String(m.name)) : `Member ${i + 1}`,
        member: m,
      })),
    [members]
  );

  const [selectedIdx, setSelectedIdx] = useState(0);
  const safeIdx = selectedIdx < memberOptions.length ? selectedIdx : 0;
  const selected = memberOptions[safeIdx] || null;
  const tests = selected?.member && Array.isArray(selected.member.tests) ? selected.member.tests : [];

  const usedCount = tests.filter((t) => t && typeof t.status === "string" && t.status.toUpperCase() === "USED").length;

  return (
    <CouponCardShell
      title={c.name || "Medical Checkup"}
      status={c.status}
      activationDate={c.activation_date}
      expiryDate={c.expiry_date}
    >
      {memberOptions.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: COLORS.mediumText, textAlign: "center", padding: "8px 0" }}>
          No members found for this family
        </p>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <label htmlFor="mcc-member-select" style={{ fontSize: 13, fontWeight: 600, color: COLORS.darkText }}>
              Member
            </label>
            <select
              id="mcc-member-select"
              value={safeIdx}
              onChange={(e) => setSelectedIdx(Number(e.target.value))}
              style={{
                border: `1px solid ${COLORS.mediumGrey}`,
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 13,
                backgroundColor: COLORS.white,
                color: COLORS.darkText,
                cursor: "pointer",
                minWidth: 200,
              }}
            >
              {memberOptions.map((opt, i) => (
                <option key={opt.key} value={i}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: COLORS.mediumText }}>
              {tests.length > 0 ? `${usedCount} of ${tests.length} used` : ""}
            </span>
          </div>

          {tests.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: COLORS.mediumText, textAlign: "center", padding: "8px 0" }}>
              No tests assigned
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 480 }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.lightGrey, color: COLORS.mediumText }}>
                    <th style={thStyle}>Test</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Used date</th>
                    <th style={thStyle}>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {tests.map((t, i) => {
                    if (!t || typeof t !== "object") return null;
                    return (
                      <tr key={t.entitlement_id ?? i} style={{ borderTop: `1px solid ${COLORS.mediumGrey}` }}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{t.test_name || "—"}</td>
                        <td style={tdStyle}>
                          <TestStatusBadge status={t.status} />
                        </td>
                        <td style={tdStyle}>{t.used_at ? formatDate(t.used_at) : "—"}</td>
                        <td style={tdStyle}>{formatReference(t.reference)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </CouponCardShell>
  );
}
